"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { matchesSearch } from "@/lib/search";

import { FieldLabel, INPUT_CLS } from "./Field";
import { RainbowUnderline } from "./RainbowUnderline";

const fieldCls = `w-full ${INPUT_CLS} transition-colors`;

export type SearchSelectOption = {
  value: string;
  label: string;
  /** Unselectable — rendered muted, with `note` saying why. */
  disabled?: boolean;
  /** Muted suffix after the label — e.g. "· added", "· no assets". */
  note?: string;
  /** A muted second line under the label — e.g. the sku. Searchable. */
  sub?: string;
  /** Trailing green status dot — a terse "this is the active/default one" marker
   *  (e.g. the system template), instead of spelling it out in `note`. */
  dot?: boolean;
  /** Extra text to match on but not display — model codes, aliases, anything a
   *  staffer might type that isn't in the label. */
  keywords?: string;
  /** Category this option belongs to. When any option carries a group, the
   *  listbox renders a header per group (the category shown ONCE) with its
   *  options under it — instead of repeating the category on every row. Pass
   *  pre-sorted by group so each group's options stay contiguous. */
  group?: string;
  /** The option's row in a table menu (see `columns`): one cell per column,
   *  in order. Cells are display only — nothing interactive, the row is the
   *  control — and unsearched: put what staff might type in `keywords`. */
  cells?: readonly ReactNode[];
};

/**
 * The office's autocomplete field: typing filters the options into a
 * listbox (top 12); picking one fires `onPick` and clears the query for
 * the next search. Disabled options stay visible so staff see why an
 * entry can't be chosen. Shared by the reservation item picker and the
 * blackout target picker — extend this, don't fork the markup.
 *
 * Keyboard (same loop as {@link Combobox}): ↓/↑ move the active option,
 * Enter picks it, Escape closes the list.
 *
 * The list is `position: fixed`, placed under the field each time it opens
 * and again on any scroll while it is open, so no ancestor's overflow can cut
 * it: a table is a scroll box, and a field in its last row opened a list
 * that stopped at the table's edge. It opens above the field instead when
 * the window leaves no room below.
 *
 * `columns` turns the menu into a table: a header row of those labels and
 * each option's `cells` under them, the columns aligned down the list. For a
 * record whose row says more than a name — a reservation with its client,
 * status, and dates. The menu then sizes to its content, so it may run wider
 * than the field. The field still shows the picked option's `label`.
 * `columnHeaders={false}` keeps the columns and drops the header row, for a
 * short list whose cells name themselves (a sticker code and a serial).
 */
/** How far the list keeps from its field, and from the window's edges. */
const GAP = 4;
const EDGE = 8;

export function SearchSelect({
  label = "search the catalog",
  ariaLabel,
  placeholder = "name, sku…",
  options,
  onPick,
  onQueryChange,
  busy = false,
  showAllWhenEmpty = false,
  blurOnPick = false,
  keepPickedLabel = false,
  defaultQuery = "",
  selectedValue,
  disabled = false,
  maxResults = 12,
  restoreQueryOnBlur = false,
  name,
  columns,
  columnHeaders = true,
}: {
  /** Visible eyebrow above the field. Pass `""` to omit it (e.g. when the
   *  field sits under its own section heading) — then set `ariaLabel`. */
  label?: string;
  /** The input's accessible name when `label` is empty (no visible eyebrow). */
  ariaLabel?: string;
  placeholder?: string;
  options: SearchSelectOption[];
  onPick: (value: string) => void;
  /** Fired as the user edits the field's text (not on pick, which clears it). For
   *  a field that doubles as an editable value — e.g. the template navigator,
   *  whose text is the open template's name and renames it on save. */
  onQueryChange?: (query: string) => void;
  /** Run the shared rainbow-underline async-save indicator on the field while a
   *  pick is being applied (e.g. an in-place action with no navigation) — the
   *  same cue the permissions / team selects use. */
  busy?: boolean;
  /** Show all options (top 12) when the query is empty — for small, bounded
   *  lists where focusing should reveal the choices. Off by default (large
   *  catalogs shouldn't dump everything). */
  showAllWhenEmpty?: boolean;
  /** Blur the field after a pick instead of re-focusing for the next search.
   *  Use for single-shot pickers (e.g. impersonation) where one pick completes
   *  the task; the multi-pick catalogs (items, blackouts) keep focus. */
  blurOnPick?: boolean;
  /** After a pick, leave the picked option's label in the field instead of
   *  clearing it. For a value field (the navigator) so re-selecting the current
   *  item re-populates the text; catalogs leave this off to clear for the next. */
  keepPickedLabel?: boolean;
  /** Seed the field with the current selection's text so it reads as populated,
   *  not just a placeholder (focus selects it all, so typing re-searches). For a
   *  single-shot picker that re-mounts on selection. */
  defaultQuery?: string;
  /** The current selection's option `value`, for a list whose labels repeat
   *  (two assets of one item) or whose field text is not an option's label
   *  (an id standing for "id · code · serial"): the list then opens on THAT
   *  option. Without it the open row is found by label, which lands on the
   *  first of the twins, or on the first row when no label matches — and
   *  Enter there silently swaps the selection for another. */
  selectedValue?: string;
  /** Render the field inert (e.g. a context where there's nothing to pick) —
   *  shown but greyed, no dropdown. */
  disabled?: boolean;
  /** Cap on how many matches the listbox shows. Defaults to 12 (large catalogs
   *  shouldn't dump everything); raise it for a small, bounded grouped list
   *  where every category should stay visible. */
  maxResults?: number;
  /** Put `defaultQuery` back when the field is left EMPTY — for a field whose
   *  text is the current selection rather than a search the caller keeps. */
  restoreQueryOnBlur?: boolean;
  /** Submit the field's text under this name when the field sits in a real
   *  form — the no-JS GET fallback (e.g. the public catalog's `?q=`). Seed the
   *  submitted value back in via `defaultQuery`. Omitted, the input stays a
   *  pure picker outside any form data. */
  name?: string;
  /** Header labels of a table menu; options then render their `cells`. */
  columns?: readonly string[];
  /** Whether the table menu heads its columns with those labels. */
  columnHeaders?: boolean;
}) {
  const [query, setQuery] = useState(defaultQuery);
  const [open, setOpen] = useState(false);
  // Whether the field's text is a search the user typed, or still the
  // selection it was seeded or left with (defaultQuery, keepPickedLabel).
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  // What the list shows for a query. `isSearching` is passed, not read, so
  // focus can ask what it is about to show before the state says so.
  const resultsFor = useCallback(
    (isSearching: boolean) => {
      // A field that reveals its list on focus reveals ALL of it while its
      // text is the current selection: filtering by that label would open the
      // menu on the one option already picked.
      if (!query.trim() || (showAllWhenEmpty && !isSearching))
        return showAllWhenEmpty ? options.slice(0, maxResults) : [];
      // Match by label, hidden keywords, AND group — order-independent, fuzzy
      // (see search.ts), so typing a category surfaces its members.
      return options
        .filter((o) =>
          matchesSearch(query, [o.label, o.sub, o.keywords, o.group]),
        )
        .slice(0, maxResults);
    },
    [options, query, showAllWhenEmpty, maxResults],
  );
  const results = useMemo(() => resultsFor(searching), [resultsFor, searching]);

  // Clamp at read time (no setState-in-effect) so a just-shrunk result set never
  // points the highlight past the end.
  // -1 is no active row: Enter picks nothing, and ↓ starts from the top.
  const activeIdx = results.length ? Math.min(active, results.length - 1) : 0;

  // Where the list sits: under the field, the field's width at least (the
  // table menu grows to its content), and above it where below has no room.
  // Parked at 0,0 first to learn where its containing block starts, which
  // is the window unless an ancestor makes one of its own (a dialog's scale).
  const place = useCallback(() => {
    const input = inputRef.current;
    const list = listRef.current;
    if (!input || !list) return;
    const box = input.getBoundingClientRect();
    list.style.left = "0px";
    list.style.top = "0px";
    list.style.minWidth = `${box.width}px`;
    const parked = list.getBoundingClientRect();
    const below = box.bottom + GAP;
    const fitsBelow = below + parked.height <= window.innerHeight - EDGE;
    const above = box.top - GAP - parked.height;
    const top = fitsBelow || above < EDGE ? below : above;
    const left = Math.max(
      EDGE,
      Math.min(box.left, window.innerWidth - parked.width - EDGE),
    );
    list.style.left = `${left - parked.left}px`;
    list.style.top = `${top - parked.top}px`;
  }, []);
  const shown = open && results.length > 0;
  useLayoutEffect(() => {
    if (!shown) return;
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [shown, place, results.length]);

  // Scroll the active option into view while navigating with the keyboard.
  // Group headers are role=presentation, so the option index lines up.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelectorAll("[role=option]")
      [activeIdx]?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActive(Math.min(activeIdx + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(Math.max(activeIdx - 1, 0));
    } else if (e.key === "Enter") {
      // A pure picker (no `name`) is not the form's text: Enter with nothing
      // to pick must not submit the form it sits in (a claim filed with no
      // asset, a claim saved mid-search). A named field is the form's query,
      // and Enter with no match submits it.
      if (!name) e.preventDefault();
      // pick() no-ops on a disabled option, so Enter can't select one.
      if (open && results[activeIdx]) {
        e.preventDefault();
        pick(results[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  function pick(o: SearchSelectOption) {
    if (o.disabled) return;
    onPick(o.value);
    // A value field keeps the picked label (so re-selecting the current item
    // re-populates the field); catalogs clear for the next pick.
    const next = keepPickedLabel ? o.label : "";
    setQuery(next);
    setSearching(false);
    onQueryChange?.(next);
    setOpen(false);
    if (blurOnPick) inputRef.current?.blur();
    else inputRef.current?.focus();
  }

  return (
    <div className="relative">
      <label className="block">
        {label && <FieldLabel>{label}</FieldLabel>}
        {/* The async-save cue reuses the shared RainbowUnderline (same as the
            permissions / team selects) — its hue line rides the field's
            border-b while a pick is applied in place. */}
        <RainbowUnderline busy={busy} className="block">
          <input
            ref={inputRef}
            type="search"
            name={name}
            // The browser's own suggestion dropdown (autofill, recent
            // searches on a named field) would cover the listbox.
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            value={query}
            disabled={disabled}
            aria-label={label ? undefined : ariaLabel}
            placeholder={placeholder}
            onChange={(e) => {
              setQuery(e.target.value);
              setSearching(true);
              setActive(0);
              setOpen(true);
              onQueryChange?.(e.target.value);
            }}
            // Select any seeded text so typing re-searches from scratch.
            onFocus={(e) => {
              setSearching(false);
              // Open on the current selection, so ↓/↑ move from where it is —
              // its place in the list focus is about to show, which is the
              // whole list only for a field that reveals it.
              // A selection past the list's cap is not in it: then no row is
              // active, rather than the first — which in a claim's picker is
              // "no asset", one Enter from clearing the asset the field shows.
              const current = resultsFor(false).findIndex((o) =>
                selectedValue !== undefined
                  ? o.value === selectedValue
                  : o.label === query,
              );
              setActive(current);
              setOpen(true);
              e.currentTarget.select();
            }}
            onKeyDown={onKeyDown}
            // Clicking the field (re)opens the list — a click on an
            // already-focused input fires no focus event, so after a pick
            // closed the list it would otherwise stay shut until a
            // defocus → refocus.
            onClick={() => setOpen(true)}
            // Delay so a click on a result lands before the list unmounts.
            onBlur={() => {
              setTimeout(() => setOpen(false), 150);
              // A field that DISPLAYS the current selection puts it back when
              // it is left empty: clearing is how you start a search, not how
              // you unset the thing — and the emptied field would otherwise
              // stop naming what the page is showing.
              if (restoreQueryOnBlur && defaultQuery && !query.trim()) {
                setQuery(defaultQuery);
                setSearching(false);
              }
            }}
            className={fieldCls}
          />
        </RainbowUnderline>
      </label>

      {open && results.length > 0 && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          // z-30: over in-content decoration (catalog card text sits at z-20),
          // still under the sticky header (z-40) and fixed popovers (z-50).
          className={`fixed z-30 max-h-72 overflow-y-auto border border-line bg-bg-muted shadow-lg ${
            columns
              ? "grid w-max max-w-[min(56rem,90vw)]"
              : "w-max max-w-[min(32rem,90vw)]"
          }`}
          style={
            columns
              ? { gridTemplateColumns: `repeat(${columns.length}, auto)` }
              : undefined
          }
        >
          {columns && columnHeaders && (
            <li
              role="presentation"
              className="sticky top-0 z-10 col-span-full grid grid-cols-subgrid border-b border-line bg-bg-muted"
            >
              {columns.map((c) => (
                <span key={c} className="label px-3 py-2 !text-fg-subtle">
                  {c}
                </span>
              ))}
            </li>
          )}
          {results.map((o, i) => {
            // A header opens each new group: the category shown once (a plain,
            // non-interactive label), with its options listed (indented) under
            // it. (Options arrive sorted by group, so a group break is simply
            // "differs from the one above".)
            const showHeader =
              o.group != null && (i === 0 || results[i - 1]?.group !== o.group);
            const grouped = o.group != null;
            return (
              <Fragment key={o.value}>
                {showHeader && (
                  <li
                    role="presentation"
                    className="px-3 pb-1 pt-2 font-mono text-[11px] tracking-wide text-fg-subtle"
                  >
                    {o.group}
                  </li>
                )}
                <li
                  className={
                    columns
                      ? "col-span-full grid grid-cols-subgrid border-b border-line last:border-b-0"
                      : undefined
                  }
                >
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    disabled={o.disabled}
                    // Keep focus on the input during the press so the blur-timeout
                    // can't close (and unmount) the list before the click lands —
                    // otherwise a pick intermittently does nothing.
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(o)}
                    className={`text-left font-mono text-xs transition-colors ${
                      columns
                        ? "col-span-full grid grid-cols-subgrid items-center"
                        : `block w-full py-2 ${grouped ? "pl-6 pr-3" : "px-3"}`
                    } ${
                      o.disabled
                        ? "cursor-not-allowed text-fg-subtle"
                        : i === activeIdx
                          ? "bg-fg/10 text-fg"
                          : "text-fg"
                    }`}
                  >
                    {columns ? (
                      columns.map((c, n) => (
                        <span key={c} className="truncate px-3 py-2">
                          {o.cells?.[n] ?? "—"}
                        </span>
                      ))
                    ) : (
                      <>
                        <span className="block truncate">
                          {o.label}
                          {o.dot && (
                            <span className="ml-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400 align-middle" />
                          )}
                          {o.note && <span className="ml-2">{o.note}</span>}
                        </span>
                        {o.sub && (
                          <span className="mt-0.5 block truncate text-fg-subtle">
                            {o.sub}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                </li>
              </Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}
